import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  Loader2,
  User,
  Users,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  UserPlus,
  FileText,
  MessageSquare,
  Shield,
  UserCog,
  Building2,
  Briefcase,
  Target,
  Edit3,
  Send,
} from 'lucide-react';
import { adminAPI, serviceRequestAPI } from '../../services/api';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { SERVICE_CONFIG } from '../../components/admin/ServiceTypeFilterCards';
import ServiceChat from '../../components/services/ServiceChat';

const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-amber-100 text-amber-700', icon: Clock },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-purple-100 text-purple-700', icon: RefreshCw },
  ON_HOLD: { label: 'On Hold', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

const STATUS_OPTIONS = [
  { value: 'PENDING_ADMIN_ASSIGNMENT', label: 'Pending Assignment' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-gray-600' },
  { value: 'medium', label: 'Medium', color: 'text-amber-600' },
  { value: 'high', label: 'High', color: 'text-orange-600' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-600' },
];

const REP_TYPE_CONFIG = {
  rep1: { label: 'Internal Rep', icon: Shield, color: 'bg-blue-100 text-blue-700' },
  rep2: { label: 'Senior Rep', icon: UserCog, color: 'bg-purple-100 text-purple-700' },
  rep3: { label: 'Partner Rep', icon: Building2, color: 'bg-amber-100 text-amber-700' },
  agent: { label: 'Agent', icon: User, color: 'bg-indigo-100 text-indigo-700' },
};

export default function AdminServiceManagement() {
  const { serviceRequestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [counselors, setCounselors] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedCounselor, setSelectedCounselor] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    fetchData();
  }, [serviceRequestId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestRes, counselorsRes] = await Promise.all([
        serviceRequestAPI.getById(serviceRequestId),
        adminAPI.getCounselors().catch(() => ({ counselors: [] })),
      ]);
      setRequest(requestRes.serviceRequest);
      setCounselors(counselorsRes.counselors || counselorsRes || []);
    } catch (error) {
      console.error('Failed to fetch service request:', error);
      toast.error('Failed to load service request');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await adminAPI.updateServiceRequestStatus(
        request.serviceRequestId || request._id,
        { status: newStatus }
      );
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAssignCounselor = async () => {
    if (!selectedCounselor) return;
    setAssigning(true);
    try {
      await adminAPI.assignServiceRequest(
        request.serviceRequestId || request._id,
        { counselorId: selectedCounselor }
      );
      toast.success('Counselor assigned');
      setAssignModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign');
    } finally {
      setAssigning(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await serviceRequestAPI.addNote(serviceRequestId, noteText.trim(), true);
      toast.success('Note added');
      setNoteText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!request) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Service Request Not Found</h2>
          <Button onClick={() => navigate('/admin/service-requests')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Service Requests
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const serviceConfig = SERVICE_CONFIG[request.serviceType] || {};
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.PENDING_ADMIN_ASSIGNMENT;
  const StatusIcon = statusConfig.icon;
  const student = request.student;
  const studentUser = student?.user;
  const counselor = request.assignedCounselorInfo;
  const agent = request.assignedAgentInfo;
  const representative = request.representative;
  const notes = request.notes || [];
  const statusHistory = request.statusHistory || [];

  const tabs = [
    { key: 'details', label: 'Details', icon: FileText },
    { key: 'notes', label: 'Notes', icon: MessageSquare, count: notes.length },
    { key: 'chat', label: 'Chat', icon: MessageSquare },
    { key: 'timeline', label: 'Timeline', icon: Clock, count: statusHistory.length },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-outfit font-bold text-gray-900">
                  {serviceConfig.name || request.serviceType?.replace(/_/g, ' ')}
                </h1>
                <Badge className={statusConfig.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="text-gray-500 text-sm mt-0.5">
                Request ID: {request.serviceRequestId} &middot; Created{' '}
                {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
              </p>
            </div>
          </div>

          {/* Status Change */}
          <div className="flex items-center gap-3">
            <Select
              value={request.status}
              onValueChange={handleStatusChange}
              disabled={statusUpdating}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Main Content: 2-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main content area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tab Navigation */}
            <div className="flex gap-1 border-b border-stone-200">
              {tabs.map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-[#F2575F] text-[#F2575F]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <TabIcon className="w-4 h-4" />
                    {tab.label}
                    {tab.count > 0 && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            {activeTab === 'details' && (
              <div className="space-y-4">
                {/* Service Details */}
                <Card className="p-5 border border-stone-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-gray-500" />
                    Service Information
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Service Type</p>
                      <p className="font-medium text-sm text-gray-900">
                        {serviceConfig.name || request.serviceType?.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Priority</p>
                      <Badge className={
                        request.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                        request.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                        request.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {request.priority || 'Normal'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Progress</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all"
                            style={{ width: `${request.progress || 0}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-600">{request.progress || 0}%</span>
                      </div>
                    </div>
                    {request.deadline && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Deadline</p>
                        <p className="font-medium text-sm text-gray-900">
                          {format(new Date(request.deadline), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Applied</p>
                      <p className="font-medium text-sm text-gray-900">
                        {format(new Date(request.appliedAt || request.createdAt), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Interaction Mode</p>
                      <Badge variant="outline" className="text-xs">
                        {request.interactionMode || 'standard'}
                      </Badge>
                    </div>
                  </div>
                </Card>

                {/* Form Data if present */}
                {request.formData && Object.keys(request.formData).length > 0 && (
                  <Card className="p-5 border border-stone-200">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      Application Data
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(request.formData).map(([key, value]) => (
                        <div key={key} className="p-3 bg-stone-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-0.5">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value || '-')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Documents */}
                {request.documents && request.documents.length > 0 && (
                  <Card className="p-5 border border-stone-200">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      Documents
                    </h3>
                    <div className="space-y-2">
                      {request.documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{doc.name || doc.filename || `Document ${idx + 1}`}</p>
                              {doc.uploadedAt && (
                                <p className="text-xs text-gray-500">
                                  {format(new Date(doc.uploadedAt), 'MMM dd, yyyy')}
                                </p>
                              )}
                            </div>
                          </div>
                          {doc.url && (
                            <Button variant="outline" size="sm" onClick={() => window.open(doc.url, '_blank')}>
                              View
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="space-y-4">
                {/* Add Note */}
                <Card className="p-4 border border-stone-200">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add an internal note..."
                        rows={3}
                        className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      />
                    </div>
                    <Button
                      onClick={handleAddNote}
                      disabled={addingNote || !noteText.trim()}
                      className="self-end"
                    >
                      {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </Card>

                {/* Notes List */}
                {notes.length > 0 ? (
                  <div className="space-y-3">
                    {[...notes].reverse().map((note, idx) => (
                      <Card key={idx} className="p-4 border border-stone-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-7 w-7">
                              <AvatarFallback className="text-xs bg-gray-200 text-gray-600">
                                {note.addedBy?.firstName?.[0] || 'A'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {note.addedBy?.firstName ? `${note.addedBy.firstName} ${note.addedBy.lastName || ''}` : 'Admin'}
                              </p>
                              {note.isInternal && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  Internal
                                </Badge>
                              )}
                            </div>
                          </div>
                          {note.createdAt && (
                            <span className="text-xs text-gray-400">
                              {formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 ml-9">{note.text}</p>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="p-8 border border-dashed border-stone-300 bg-stone-50/50 text-center">
                    <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No notes yet</p>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'chat' && (
              <Card className="border border-stone-200 overflow-hidden" style={{ minHeight: '500px' }}>
                <ServiceChat
                  serviceRequestId={request.serviceRequestId || serviceRequestId}
                  counselor={counselor}
                  agent={agent}
                  isStaffView={true}
                  readOnly={false}
                />
              </Card>
            )}

            {activeTab === 'timeline' && (
              <div className="space-y-4">
                {statusHistory.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-stone-200" />
                    <div className="space-y-4">
                      {[...statusHistory].reverse().map((entry, idx) => {
                        const config = STATUS_CONFIG[entry.status] || STATUS_CONFIG.PENDING_ADMIN_ASSIGNMENT;
                        const EntryIcon = config.icon;
                        return (
                          <div key={idx} className="relative flex items-start gap-4 ml-0">
                            <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}>
                              <EntryIcon className="w-3.5 h-3.5" />
                            </div>
                            <Card className="flex-1 p-4 border border-stone-200">
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-medium text-sm text-gray-900">{config.label}</p>
                                {entry.changedAt && (
                                  <span className="text-xs text-gray-400">
                                    {format(new Date(entry.changedAt), 'MMM dd, yyyy HH:mm')}
                                  </span>
                                )}
                              </div>
                              {entry.note && <p className="text-sm text-gray-600">{entry.note}</p>}
                              {entry.changedBy && (
                                <p className="text-xs text-gray-400 mt-1">
                                  by {entry.changedBy.firstName || entry.changedBy} {entry.changedBy.lastName || ''}
                                </p>
                              )}
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <Card className="p-8 border border-dashed border-stone-300 bg-stone-50/50 text-center">
                    <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No status changes recorded</p>
                  </Card>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-4">
            {/* Student Info */}
            <Card className="p-5 border border-stone-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                Student
              </h4>
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-gradient-to-br from-blue-400 to-blue-600 text-white font-medium">
                    {(studentUser?.firstName || student?.firstName)?.[0]}
                    {(studentUser?.lastName || student?.lastName)?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">
                    {studentUser?.firstName || student?.firstName}{' '}
                    {studentUser?.lastName || student?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {studentUser?.email || student?.email}
                  </p>
                </div>
              </div>
              {student?.studentId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate(`/admin/students/${student.studentId}`)}
                >
                  View Student Profile
                </Button>
              )}
            </Card>

            {/* Assigned Counselor */}
            <Card className="p-5 border border-stone-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4 text-green-600" />
                  Counselor
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedCounselor(counselor?.userId || '');
                    setAssignModalOpen(true);
                  }}
                >
                  <Edit3 className="w-3 h-3 mr-1" />
                  {counselor ? 'Change' : 'Assign'}
                </Button>
              </div>
              {counselor ? (
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-gradient-to-br from-green-400 to-green-600 text-white text-sm font-medium">
                      {counselor.firstName?.[0]}{counselor.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      {counselor.firstName} {counselor.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{counselor.email}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No counselor assigned</p>
              )}
            </Card>

            {/* Representative / Agent */}
            {(representative || agent || request.isAgentInitiated) && (
              <Card className="p-5 border border-stone-200">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-indigo-600" />
                  {representative ? 'Representative' : 'Agent'}
                </h4>
                {(representative || agent) ? (
                  <div>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-gradient-to-br from-indigo-400 to-indigo-600 text-white text-sm font-medium">
                          {(representative || agent).firstName?.[0]}
                          {(representative || agent).lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm text-gray-900">
                          {(representative || agent).firstName} {(representative || agent).lastName}
                        </p>
                        <p className="text-xs text-gray-500">{(representative || agent).email}</p>
                      </div>
                    </div>
                    {representative?.role && REP_TYPE_CONFIG[representative.role] && (
                      <Badge className={`mt-2 ${REP_TYPE_CONFIG[representative.role].color}`}>
                        {REP_TYPE_CONFIG[representative.role].label}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                    Agent Initiated
                  </Badge>
                )}
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="p-5 border border-stone-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-gray-500" />
                Quick Actions
              </h4>
              <div className="space-y-2">
                <Select
                  value={request.priority || 'medium'}
                  onValueChange={async (val) => {
                    try {
                      await adminAPI.updateServiceRequestPriority(
                        request.serviceRequestId || request._id, val
                      );
                      toast.success('Priority updated');
                      fetchData();
                    } catch { toast.error('Failed to update priority'); }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Set Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className={opt.color}>{opt.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(`/admin/service-requests`)}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  All Service Requests
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Assign Counselor Dialog */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Assign Counselor
            </DialogTitle>
            <DialogDescription>
              Select a counselor for this service request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedCounselor} onValueChange={setSelectedCounselor}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a counselor" />
              </SelectTrigger>
              <SelectContent>
                {counselors.map((c) => (
                  <SelectItem key={c.userId || c._id} value={c.userId || c._id}>
                    {c.firstName} {c.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
              <Button onClick={handleAssignCounselor} disabled={assigning || !selectedCounselor}>
                {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
